import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs/internal/Observable';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { environment } from '../../../../environments/environment';
import { CompanyinboxService } from '../companies/services/companyinbox.service';
import { BaseApiService } from '../../../shared/services/api';
import { filter, take } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class InboxintelService {
    scanInProgress = false;
    hasInboxIntel = false;
    enableAuthentication = false;
    externalPrincipalId = '';
    newCompanies = '';
    newContacts = '';
    contactsSavedToSI = '';
    companiesSavedToSI = '';
    contactsExportedToSF = '';
    companiesExportedToSF = '';
    oAuthUserId;
    isVerifiedTab = 'all';
    isVerifiedTabContacts = 'all';
    isVerifiedTabCompanies = 'all';
    activeTab = 1;
    companiesCountHuman = 0;
    companiesCountMachine = 0;
    companiesCountAll = 0;
    contactsCountHuman = 0;
    contactsCountHumanAndEmail = 0;
    contactsCountMachine = 0;
    contactsCountAll = 0;
    isExistingUser = true;
    status$: Observable<any>;

    protected readonly statusSubject: BehaviorSubject<any> = new BehaviorSubject({
        isExistingUser: this.isExistingUser,
        enableAuthentication: this.enableAuthentication,
        scanInProgress: this.scanInProgress,
        hasInboxIntel: this.hasInboxIntel,
    });

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly companyInboxService: CompanyinboxService,
        private readonly toastr: ToastrService,
        private readonly baseApiService: BaseApiService
    ) {
        this.externalPrincipalId = sessionStorage.getItem('externalPrincipalId');
        this.status$ = this.statusSubject.asObservable();
    }

    get isVerifiedTabAccuracyTier(): string {
        return this.activeTab === 0 ? this.isVerifiedTabCompanies : this.isVerifiedTabContacts;
    }

    // TODO: check
    scanGmailInbox() {
        if (!this.externalPrincipalId) {
            let jobStatus = '';
            this.scanGmail().subscribe((res: any) => {
                const externalPrincipals = res.externalPrincipals;
                if (res.externalPrincipals.length > 0) {
                    externalPrincipals.map((item) => {
                        jobStatus = item.jobStatus;
                        if (jobStatus === 'Completed') {
                            this.externalPrincipalId = item.externalPrincipalId;
                            sessionStorage.setItem('externalPrincipalId', this.externalPrincipalId);
                            sessionStorage.setItem('oAuthUserId', item.oAuthUserId);
                            this.isVerifiedTab = 'all';
                            this.activeTab = 1;
                            this.getStats();
                            // this.checkCompaniesList();
                            this.isExistingUser = false;
                            this.hasInboxIntel = true;
                            this.enableAuthentication = false;
                            this.scanInProgress = false;
                            return false;
                        } else if (jobStatus === 'JobFailed_NeedsReoauth') {
                            if (externalPrincipals.length === 1) {
                                this.toastr.error('InboxIntel requires reauthorization. Please connect your email inbox again.');
                                this.isExistingUser = false;
                                this.enableAuthentication = true;
                                this.scanInProgress = false;
                                this.hasInboxIntel = false;

                            }
                        } else {
                            setTimeout(() => {
                                this.authenticationService.checkUserAlreadyValidLogin().pipe(
                                    take(1),
                                    filter(Boolean)
                                ).subscribe(() => {
                                    this.scanGmailInbox();
                                })
                            }, 4000);
                        }
                    });
                } else {
                    this.enableAuthentication = true;
                    this.scanInProgress = false;
                    this.isExistingUser = false;
                }

                this.updateStatus();
            }, (error) => {
                this.toastr.error(error.error.response);
            });
        }
    }

    getStats() {
        this.fetchInboxIntelStats().subscribe(res => {
            this.newContacts = res.newContacts;
            this.newCompanies = res.newCompanies;
            this.contactsSavedToSI = res.contactsSavedToSalesIntel;
            this.contactsExportedToSF = res.contactsSavedToSalesforce;
            this.companiesSavedToSI = res.companiesSavedToSalesIntel;
            this.companiesExportedToSF = res.companiesSavedToSalesforce;
            this.companiesCountHuman = res.companiesCountHuman;
            this.companiesCountMachine = res.companiesCountMachine;
            this.companiesCountAll = res.companiesCountAll;
            this.contactsCountHuman = res.contactsCountHuman;
            this.contactsCountHumanAndEmail = res.contactsCountHumanAndEmail;
            this.contactsCountMachine = res.contactsCountMachine;
            this.contactsCountAll = res.contactsCountAll;
        });
    }
    getExternalPrincipalId() {
        if (sessionStorage.getItem('externalPrincipalId') !== null) {
            this.getStats();
            this.enableAuthentication = false;
            this.hasInboxIntel = true;
            this.scanInProgress = false;
            this.isExistingUser = false;

            this.updateStatus();
        } else if (!this.externalPrincipalId) {
            this.scanGmailInbox();
        }
    }

    checkCompaniesList() {
        this.scanInProgress = true;
        this.companyInboxService.getInboxIntelCompanies(this.externalPrincipalId, 1, 'all').subscribe((companies) => {
            if (companies.response.length === 0) {
                this.enableAuthentication = true;
                this.hasInboxIntel = false;
                this.scanInProgress = false;
            } else if (companies.response.length > 0) {
                this.enableAuthentication = false;
                this.hasInboxIntel = true;
                this.scanInProgress = false;
            }

            this.updateStatus();
        });
    }

    public authenticate(code: string, emailType: string): Observable<any> {
        const request = {
            whoAmIAccountId: this.authenticationService.user.account_id,
            whoAmIEmail: this.authenticationService.user.email,
            whoAmIId: this.authenticationService.user.id,
            oAuthAccessToken: code,
            applicationName: environment.google.applicationName,
            applicationVersion: 'v1',
            emailType: emailType
        };
        return this.baseApiService.post<any>('service/contactcloud/inboxIntel/externalprincipals/oauth', request, { excludeImpersonate: true });
    }

    public scanGmail() {
        return this.baseApiService.get<any>('service/contactcloud/inboxIntel/externalprincipals', { excludeImpersonate: true });
    }

    public fetchInboxIntelStats() {
        const param = sessionStorage.getItem('externalPrincipalId');
        return this.baseApiService.get<any>('service/contactcloud/inboxIntel/stats?externalPrincipalId=' + param, { excludeImpersonate: true });
    }

    private updateStatus(): void {
        this.statusSubject.next({
            isExistingUser: this.isExistingUser,
            enableAuthentication: this.enableAuthentication,
            scanInProgress: this.scanInProgress,
            hasInboxIntel: this.hasInboxIntel,
        })
    }
}
